import { Button, Modal, Spin, Alert } from "antd";
import { useCallback, useContext, useEffect, useState } from "react";
import { ConfiguratorContext } from "../../context";
import { Quote } from "../../api/models";

const OrderSpecsModal = (props: {
  quote: Quote | undefined;
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const { quote, open, onConfirm, onCancel } = props;
  const configurator = useContext(ConfiguratorContext);
  const [pdfUrl, setPdfUrl] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const loadPdf = useCallback(async () => {
    if (!quote || !open) return;
    setLoading(true);
    setError(undefined);

    try {
      const url = configurator.api.getExportQuotePdfUrl(quote.displayRevisionId, {
        inventory: false,
        technicalSpecification: true,
        pricingSummary: true,
        priceForDealer: true,
        gvwr: true,
        componentLocation: true,
        performance: true,
        paint: true,
        cancellationPolicy: true,
        notes: true,
      });
      const resp = await configurator.api.axios.get(url, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const objectUrl = URL.createObjectURL(blob);
      setPdfUrl(objectUrl);
    } catch {
      setError("Unable to load order specifications. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [quote?.displayRevisionId, open]);

  useEffect(() => {
    if (open) {
      loadPdf();
    }
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(undefined);
      }
    };
  }, [open]);

  return (
    <Modal
      title="Order Specifications"
      open={open}
      onCancel={onCancel}
      width="80vw"
      centered
      destroyOnClose
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: ".5rem" }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" disabled={!pdfUrl} onClick={onConfirm}>
            Confirm
          </Button>
        </div>
      }
    >
      {loading && (
        <div style={{ textAlign: "center", padding: "4rem" }}>
          <Spin size="large" />
          <div style={{ marginTop: "1rem" }}>Loading specifications...</div>
        </div>
      )}
      {error && <Alert type="error" message={error} />}
      {pdfUrl && (
        <iframe
          src={`${pdfUrl}#toolbar=1&navpanes=0`}
          style={{ width: "100%", height: "75vh", border: "1px solid #d9d9d9", borderRadius: "4px" }}
          title="Order Specifications"
        />
      )}
    </Modal>
  );
};

export default OrderSpecsModal;
